/**
 * @quotech/ui/addressAutocomplete — Provider implementations
 *
 * Two built-in providers:
 *
 * 1. {@link nominatimProvider} — OpenStreetMap's free Nominatim API.
 *    No API key required, public endpoint. Rate-limited by IP — fine
 *    for one-user-at-a-time interactive autocomplete (1 req/keystroke
 *    after a 350ms debounce in the component). Requires a sane
 *    `User-Agent` per Nominatim's usage policy.
 *
 * 2. {@link makeGooglePlacesProvider} — Google Places Autocomplete +
 *    Place Details. Higher-quality results + structured address
 *    components, but requires a billing-enabled API key. Pass the
 *    key in via the factory or set `VITE_GOOGLE_PLACES_API_KEY` (the
 *    consumer reads the env var and decides which provider to use).
 *
 * Picking a provider at runtime — see {@link resolveProvider}.
 */

import type { AddressProvider, AddressSuggestion } from './types'

// ---------------------------------------------------------------------------
// Nominatim (OpenStreetMap) — default, free, no key
// ---------------------------------------------------------------------------

interface NominatimResponse {
  place_id: number | string
  display_name: string
  lat: string
  lon: string
  address?: {
    road?: string
    house_number?: string
    suburb?: string
    village?: string
    town?: string
    city?: string
    state?: string
    postcode?: string
    country?: string
  }
}

// Nominatim spells states out ("New South Wales"); an address line reads the
// way Australians write it ("Sydney NSW 2000").
const AU_STATE_CODES: Record<string, string> = {
  'New South Wales': 'NSW',
  Victoria: 'VIC',
  Queensland: 'QLD',
  'Western Australia': 'WA',
  'South Australia': 'SA',
  Tasmania: 'TAS',
  'Australian Capital Territory': 'ACT',
  'Northern Territory': 'NT',
}

/**
 * One readable line from structured parts: "12 George Street, Sydney NSW
 * 2000, Australia". f(parts, fallback) -> string. Falls back to the
 * provider's own string when there is neither a street nor a locality.
 */
export function formatAddressLine(
  parts: NonNullable<AddressSuggestion['parts']>,
  fallback: string,
): string {
  const locality = parts.suburb ?? parts.city ?? null
  if (!parts.street && !locality) return fallback
  const state = parts.state ? (AU_STATE_CODES[parts.state] ?? parts.state) : null
  const area = [locality, state, parts.postcode].filter(Boolean).join(' ')
  return [parts.street, area, parts.country].filter(Boolean).join(', ')
}

/**
 * Nominatim provider. `countryRestriction` (ISO-2, e.g. 'au') becomes
 * `countrycodes`, which is what makes "12 george st" resolve to a street in
 * Australia instead of a city on the other side of the world.
 */
export function makeNominatimProvider({
  countryRestriction,
}: { countryRestriction?: string } = {}): AddressProvider {
  return {
    name: 'OpenStreetMap',
    search: (query, signal) => searchNominatim(query, signal, countryRestriction),
  }
}

export const nominatimProvider: AddressProvider = makeNominatimProvider()

async function searchNominatim(
  query: string,
  signal: AbortSignal,
  countryRestriction?: string,
): Promise<AddressSuggestion[]> {
  const url = new URL('https://nominatim.openstreetmap.org/search')
  url.searchParams.set('q', query)
  url.searchParams.set('format', 'json')
  url.searchParams.set('addressdetails', '1')
  url.searchParams.set('limit', '6')
  if (countryRestriction) {
    url.searchParams.set('countrycodes', countryRestriction.toLowerCase())
  }
  // Nominatim's usage policy asks for a meaningful User-Agent. We
  // can't set one from a browser fetch — the policy explicitly
  // permits browser-side use as long as request volume stays
  // reasonable (it does — 350ms debounce + 6 suggestions).
  const res = await fetch(url.toString(), {
    signal,
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) return []
  const rows = (await res.json()) as NominatimResponse[]
  return rows.map((r) => {
    const a = r.address ?? {}
    const street =
      [a.house_number, a.road].filter(Boolean).join(' ') || null
    const parts = {
      street,
      suburb: a.suburb ?? a.village ?? null,
      city: a.city ?? a.town ?? null,
      state: a.state ?? null,
      postcode: a.postcode ?? null,
      country: a.country ?? null,
    }
    return {
      id: String(r.place_id),
      display: formatAddressLine(parts, r.display_name),
      parts,
      lat: r.lat ? Number(r.lat) : null,
      lon: r.lon ? Number(r.lon) : null,
    } satisfies AddressSuggestion
  })
}

// ---------------------------------------------------------------------------
// Google Places — opt-in, higher quality, requires key
// ---------------------------------------------------------------------------

/**
 * Google Places provider. Uses the modern "Places (New)" REST API
 * (POST https://places.googleapis.com/v1/places:autocomplete +
 * /v1/places/{placeId}). Required field mask kept minimal so the
 * billed-bytes-per-request stays low.
 *
 * Pass the API key once and call the returned object as a provider:
 *
 *   const google = makeGooglePlacesProvider({ apiKey })
 *   const results = await google.search('123 elizabeth', signal)
 */
export function makeGooglePlacesProvider({
  apiKey,
  countryRestriction,
  fallback,
}: {
  apiKey: string
  /** Two-letter ISO country code (e.g. 'au'). Defaults to unrestricted. */
  countryRestriction?: string
  /**
   * Answers when Google can't. A key whose project has Places API (New)
   * disabled gets a 403 on every request; without a fallback that reads as
   * "no addresses exist" and the field silently degrades to free text.
   */
  fallback?: AddressProvider
}): AddressProvider {
  // A config error (bad key, API disabled, billing off) will not fix itself
  // mid-session, so stop paying a failed round trip on every keystroke.
  let googleDisabled = false
  return {
    get name() {
      return googleDisabled && fallback ? fallback.name : 'Google Places'
    },
    async search(query, signal) {
      if (googleDisabled && fallback) return fallback.search(query, signal)
      const body: Record<string, unknown> = { input: query }
      if (countryRestriction) {
        body.includedRegionCodes = [countryRestriction.toLowerCase()]
      }
      let res: Response
      try {
        res = await fetch(
          'https://places.googleapis.com/v1/places:autocomplete',
          {
            method: 'POST',
            signal,
            headers: {
              'Content-Type': 'application/json',
              'X-Goog-Api-Key': apiKey,
              // Field mask — request only the bits we render in the
              // dropdown. Keeps Google's billed bytes-per-request down.
              'X-Goog-FieldMask':
                'suggestions.placePrediction.placeId,suggestions.placePrediction.text,suggestions.placePrediction.structuredFormat',
            },
            body: JSON.stringify(body),
          },
        )
      } catch (err) {
        if (!fallback || (err as DOMException)?.name === 'AbortError') throw err
        return fallback.search(query, signal)
      }
      if (!res.ok) {
        if (!fallback) return []
        if (GOOGLE_CONFIG_ERRORS.has(res.status)) googleDisabled = true
        return fallback.search(query, signal)
      }
      const data = (await res.json()) as {
        suggestions?: Array<{
          placePrediction?: {
            placeId: string
            text: { text: string }
            structuredFormat?: {
              mainText?: { text: string }
              secondaryText?: { text: string }
            }
          }
        }>
      }
      const list = data.suggestions ?? []
      return list
        .map((s): AddressSuggestion | null => {
          const p = s.placePrediction
          if (!p) return null
          return {
            id: p.placeId,
            display: p.text.text,
            // The autocomplete endpoint doesn't return structured
            // parts — they require a separate Place Details call.
            // We populate them lazily in `getDetails` below, which the
            // component invokes once when the user picks a row (one
            // detail call on commit is far cheaper than one per
            // keystroke).
            parts: undefined,
          }
        })
        .filter((x): x is AddressSuggestion => x != null)
    },
    // Enrich a picked suggestion with structured components + coords.
    // Google's Place Details (New) GET returns addressComponents +
    // location for a placeId; we map them onto AddressSuggestion so the
    // consumer can persist a structured address (street/city/state/
    // postcode/country/lat/lon), matching what Nominatim returns from
    // `search`. Field mask kept minimal to keep billed bytes low.
    async getDetails(suggestion, signal) {
      if (!suggestion.id) return suggestion
      const res = await fetch(
        `https://places.googleapis.com/v1/places/${encodeURIComponent(
          suggestion.id,
        )}`,
        {
          signal,
          headers: {
            'X-Goog-Api-Key': apiKey,
            'X-Goog-FieldMask':
              'formattedAddress,location,addressComponents',
          },
        },
      )
      if (!res.ok) return suggestion
      const d = (await res.json()) as GooglePlaceDetails
      const comps = d.addressComponents ?? []
      const street =
        [pickComponent(comps, 'street_number'), pickComponent(comps, 'route')]
          .filter(Boolean)
          .join(' ') || null
      return {
        ...suggestion,
        // Prefer Google's canonical formatted address; keep the
        // autocomplete display as a fallback.
        display: d.formattedAddress ?? suggestion.display,
        parts: {
          street,
          suburb:
            pickComponent(comps, 'sublocality_level_1') ??
            pickComponent(comps, 'sublocality') ??
            pickComponent(comps, 'neighborhood') ??
            null,
          city:
            pickComponent(comps, 'locality') ??
            pickComponent(comps, 'postal_town') ??
            null,
          // State abbreviation (e.g. "NSW") reads cleaner in an address
          // line than the full "New South Wales".
          state: pickComponent(comps, 'administrative_area_level_1', 'short') ?? null,
          postcode: pickComponent(comps, 'postal_code') ?? null,
          country: pickComponent(comps, 'country') ?? null,
        },
        lat: d.location?.latitude ?? null,
        lon: d.location?.longitude ?? null,
      } satisfies AddressSuggestion
    },
  }
}

// ---------------------------------------------------------------------------
// Google Place Details helpers
// ---------------------------------------------------------------------------

// Statuses that mean the key or project is misconfigured, not a blip.
const GOOGLE_CONFIG_ERRORS = new Set([400, 401, 403, 404])

interface GoogleAddressComponent {
  longText?: string
  shortText?: string
  types?: string[]
}

interface GooglePlaceDetails {
  formattedAddress?: string
  location?: { latitude?: number | null; longitude?: number | null }
  addressComponents?: GoogleAddressComponent[]
}

/**
 * Pull the long (or short) text of the first address component matching
 * `type` (e.g. 'locality', 'postal_code'). Returns null when absent so
 * the caller can chain `?? fallback`.
 */
function pickComponent(
  components: GoogleAddressComponent[],
  type: string,
  variant: 'long' | 'short' = 'long',
): string | null {
  const c = components.find((x) => (x.types ?? []).includes(type))
  if (!c) return null
  const primary = variant === 'short' ? c.shortText : c.longText
  return primary ?? c.longText ?? c.shortText ?? null
}

// ---------------------------------------------------------------------------
// Resolve which provider to use at runtime
// ---------------------------------------------------------------------------

/**
 * Pick the right provider based on configuration. Order:
 *
 *   1. If a `provider` is explicitly passed (e.g. by a Storybook
 *      story or a tenant override), use it.
 *   2. Else if a Google Places API key is supplied, use Google, with
 *      Nominatim answering whenever Google errors.
 *   3. Else fall back to Nominatim (free, no key required).
 *
 * The component calls this lazily on first keystroke, not at mount,
 * so feature flags can flip mid-session.
 */
export function resolveProvider({
  provider,
  googleApiKey,
  countryRestriction,
}: {
  provider?: AddressProvider
  googleApiKey?: string
  countryRestriction?: string
} = {}): AddressProvider {
  if (provider) return provider
  const openStreetMap = makeNominatimProvider({ countryRestriction })
  if (googleApiKey) {
    return makeGooglePlacesProvider({
      apiKey: googleApiKey,
      countryRestriction,
      fallback: openStreetMap,
    })
  }
  return openStreetMap
}
